import { useEffect, useRef } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuthStore, GoogleUser } from '../stores/authStore';
import {
  claimUnassignedSyncEntities,
  getRememberedGoogleIdentity,
  setRememberedGoogleIdentity,
} from '../services/localStorageService';
import { setDriveAccountId } from '../services/googleDriveService';

const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
export const DRIVE_RESTORE_TIMEOUT_MS = 10000;

export async function fetchGoogleUser(accessToken: string): Promise<GoogleUser> {
  const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    throw new Error('Could not load the Google account profile');
  }

  const profile = (await response.json()) as Partial<GoogleUser>;
  if (!profile.sub) {
    throw new Error('Google account profile did not include an account ID');
  }

  return {
    sub: profile.sub,
    name: profile.name ?? 'Google User',
    email: profile.email ?? '',
    picture: profile.picture ?? '',
  };
}

export function useDriveSession(): void {
  const restoreTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restoreStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setUser = useAuthStore((state) => state.setUser);
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const setConnectionStatus = useAuthStore((state) => state.setConnectionStatus);
  const setSyncStatus = useAuthStore((state) => state.setSyncStatus);

  const finishRestore = () => {
    if (restoreTimerRef.current !== null) {
      clearTimeout(restoreTimerRef.current);
      restoreTimerRef.current = null;
    }
  };

  const silentLogin = useGoogleLogin({
    scope: DRIVE_SCOPE,
    flow: 'implicit',
    prompt: '',
    onSuccess: async (response) => {
      finishRestore();
      const remembered = getRememberedGoogleIdentity();
      const tokenResponse = response as { access_token: string; expires_in?: number };
      let profile: GoogleUser;
      try {
        profile = await fetchGoogleUser(tokenResponse.access_token);
        if (!remembered || profile.sub !== remembered.sub) {
          setAccessToken(null);
          setConnectionStatus('reauth-required');
          return;
        }

        setDriveAccountId(profile.sub);
        claimUnassignedSyncEntities(profile.sub);
          setRememberedGoogleIdentity(profile);
        setUser(profile);
        setAccessToken(
          tokenResponse.access_token,
          new Date(Date.now() + (tokenResponse.expires_in ?? 3540) * 1000)
        );
        setConnectionStatus('connected');
        setSyncStatus('idle');
      } catch (error) {
        setAccessToken(null);
        setConnectionStatus('reauth-required');
        setSyncStatus('error', error instanceof Error ? error.message : 'Drive reconnect failed');
        return;
      }
    },
    onError: () => {
      finishRestore();
      setAccessToken(null);
      setConnectionStatus('reauth-required');
    },
  });

  const silentLoginRef = useRef(silentLogin);
  silentLoginRef.current = silentLogin;

  useEffect(() => {
    if (!getRememberedGoogleIdentity()) return;

    restoreTimerRef.current = setTimeout(() => {
      restoreTimerRef.current = null;
      if (useAuthStore.getState().connectionStatus !== 'restoring') return;
      setAccessToken(null);
      setConnectionStatus('reauth-required');
    }, DRIVE_RESTORE_TIMEOUT_MS);
    restoreStartTimerRef.current = setTimeout(() => {
      restoreStartTimerRef.current = null;
      silentLoginRef.current();
    }, 0);

    return () => {
      if (restoreStartTimerRef.current !== null) {
        clearTimeout(restoreStartTimerRef.current);
        restoreStartTimerRef.current = null;
      }
      if (restoreTimerRef.current !== null) {
        clearTimeout(restoreTimerRef.current);
        restoreTimerRef.current = null;
      }
    };
  }, [setAccessToken, setConnectionStatus]);
}

export const DriveSessionRestorer = (): null => {
  useDriveSession();
  return null;
};